import { Statsig } from 'statsig-react';
import brandConfig from '../config/brand';
import { isPreviewHost } from '../config/brand-manifest';
import { deriveCookieDomain, getStatsigStableId, trackEvent } from './statsig';
import { splitAssignmentIdForPayload } from './splitTest';

export const SERVICE_OPTION_EXPERIMENT = 'pricing_tier_test_v1';
export const CHEM_BRUSH = 'monthly-pool-chem-brush';
export const serviceOptionFormVersion = offer => `Sacramento service options ${offer.arm}`;
export const SERVICE_OPTION_HOSTS = ['quote.cabanapools.com', 'quote.codyspoolservice.com'];
const LANE_KEY = '_cabana_quote_lane';

export function readQuoteLane() {
  let cookie;
  try { cookie = document.cookie.match(/(?:^|;\s*)_cabana_quote_lane=([^;]*)/)?.[1]; } catch { /* try storage */ }
  let stored;
  try { stored = localStorage.getItem(LANE_KEY); } catch { /* cookies may still work */ }
  for (const raw of [cookie, stored]) {
    try {
      const marker = JSON.parse(decodeURIComponent(raw));
      if (marker.stableId === getStatsigStableId() && ['v5', 'tier'].includes(marker.lane)) return marker.lane;
    } catch { /* ignore malformed or absent markers */ }
  }
  return null;
}

function hasDurableIdentity() {
  const stableId = getStatsigStableId();
  try {
    if (document.cookie.match(/(?:^|;\s*)_cabana_sid=([^;]*)/)?.[1] === stableId) return true;
  } catch { /* try storage */ }
  try { return localStorage.getItem('_cabana_sid') === stableId; } catch { return false; }
}

// Never hold the browser lock across pricing or event requests. It only
// serializes the durable lane choice shared by tabs of this quote origin.
export async function claimQuoteLane(lane) {
  const claim = () => {
    const current = readQuoteLane();
    if (current) return current;
    if (!hasDurableIdentity()) return null;
    const marker = encodeURIComponent(JSON.stringify({ stableId: getStatsigStableId(), lane }));
    const domain = deriveCookieDomain();
    try {
      document.cookie = `${LANE_KEY}=${marker};${domain ? ` domain=${domain};` : ''} path=/; max-age=31536000; SameSite=Lax`;
    } catch { /* try the same fallback as the stableID */ }
    if (!readQuoteLane()) {
      try { localStorage.setItem(LANE_KEY, marker); } catch { /* fail closed for new tier enrollment */ }
    }
    return readQuoteLane();
  };
  if (!['v5', 'tier'].includes(lane)) return null;
  if (!navigator.locks?.request) return readQuoteLane() || (lane === 'v5' ? claim() : null);
  try { return await navigator.locks.request(LANE_KEY, claim); } catch { return readQuoteLane(); }
}

export function isServiceOptionsEnabled() {
  try { return Statsig.checkGateWithExposureLoggingDisabled('sac_service_options_enabled') === true; }
  catch { return false; }
}

export function qualifiesForServiceOptions(data, hostname = window.location.hostname) {
  const host = isPreviewHost(hostname) ? `quote.${brandConfig.siteOrigin}` : hostname;
  return SERVICE_OPTION_HOSTS.includes(host)
    && data?.poolType === 'residential'
    && Array.isArray(data.service) && data.service.length === 1
    && data.service[0] === 'regular_pool_spa_service'
    && ['pool_only', 'pool_spa', 'pool_hot_tub'].includes(data.poolOption)
    && !data.poolOption1?.includes('ground_pool')
    && ['small', 'medium', 'large'].includes(data.poolSize);
}

export function keepsLegacySelfServe(prepared) {
  return prepared?.pricingActive !== true;
}

export async function prepareServiceOffer(form, inactive, v5Exposed, hostname = window.location.hostname) {
  const fallback = () => ({ allowLegacy: readQuoteLane() !== 'tier' });
  if (v5Exposed || readQuoteLane() === 'v5') return { allowLegacy: true };
  if (!isServiceOptionsEnabled() || !qualifiesForServiceOptions(form, hostname)
      || !navigator.locks?.request || !hasDurableIdentity()) return fallback();
  // Only inputs affecting eligibility, pricing or customer matching invalidate
  // a negative result. Later contact/email steps must not repeat the preflight.
  const key = JSON.stringify([hostname, form.zipCode, form.poolType, form.service, form.poolOption,
    form.poolOption1, form.poolSize, form.address, form.personName, form.phone]);
  if (inactive.current?.key === key) return readQuoteLane() === 'tier' ? fallback() : inactive.current.result;
  try {
    const prepared = await serviceOptionsRequest('prepare', { form });
    // Another tab may have chosen v5 while this request was in flight.
    if (readQuoteLane() === 'v5') return { allowLegacy: true };
    if (prepared.state === 'existing_quote') return { allowLegacy: false, existingQuote: prepared };
    if (prepared.eligible && prepared.offer) {
      if (await claimQuoteLane('tier') !== 'tier') return fallback();
      return { allowLegacy: false, offer: prepared.offer };
    }
    const result = { allowLegacy: keepsLegacySelfServe(prepared) };
    inactive.current = { key, result };
    return readQuoteLane() === 'tier' ? fallback() : result;
  } catch (error) {
    return error.state === 'quote_review_pending' || readQuoteLane() === 'tier'
      ? { allowLegacy: false, retry: true, reviewPending: error.state === 'quote_review_pending' } : fallback();
  }
}

export function getQuoteSession() {
  const match = document.cookie.match(/(?:^|;\s*)cabana_quote_session=([a-f0-9-]{36})(?:;|$)/);
  return match ? match[1] : null;
}

export async function serviceOptionsRequest(action, data = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), action === 'prepare' ? 3000 : 45000);
  // Preparation holds the visitor on the page, so its latency and timeout
  // rate are reported to size that limit from real traffic.
  const started = Date.now();
  let outcome = 'error';
  try {
    const response = await fetch('/api/service-options', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...data, action, site_origin: brandConfig.siteOrigin,
      statsig_stable_id: getStatsigStableId(),
      quote_session: getQuoteSession(),
      // Cabana (control) arm of the split test; omitted when not in test.
      split_assignment_id: splitAssignmentIdForPayload(),
    }),
      signal: controller.signal,
    });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      const error = new Error(result.message || 'We could not save your quote. Please try again.');
      error.state = result.state;
      throw error;
    }
    outcome = 'ok';
    return result;
  } catch (error) {
    if (controller.signal.aborted) outcome = 'timeout';
    throw error;
  } finally {
    clearTimeout(timeout);
    if (action === 'prepare') trackEvent('sac_prepare_timing', { ms: String(Date.now() - started), outcome });
  }
}
